// Pieces shared by the Leaflets page's tabs: people tags and pills, progress
// bars, proof pictures, the zones map layer and the shape editor used to draw
// zones and routes.
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/auth';
import { Pill, ModalShell, Button } from '../lib/ui';
import {
  PERSON_STATUS, JOB_STATUS, RUN_STATE, runState, jobProgress, formatCount, COLOURS,
} from '../lib/leaflets';

export const byId = (list) => Object.fromEntries((list || []).map(x => [x._id, x]));

export const PersonTag = ({ person, onClick }) => (
  <span className={`lf-person${onClick ? ' is-link' : ''}`} onClick={onClick}>
    <i className="lf-swatch" style={{ background: person?.colour || COLOURS[0] }} />
    {person?.name || 'Someone'}
  </span>
);

export const PersonPill = ({ status }) => <Pill tone={PERSON_STATUS[status]?.tone}>{PERSON_STATUS[status]?.label || status}</Pill>;
export const JobPill = ({ job }) => <Pill tone={JOB_STATUS[job.status]?.tone}>{JOB_STATUS[job.status]?.label || job.status}</Pill>;
export const RunPill = ({ run }) => {
  const s = RUN_STATE[runState(run)];
  return <Pill tone={s?.tone}>{s?.label || run.status}</Pill>;
};

// Approved (green), waiting to be checked (amber) and still to go, out of the job's leaflets.
export function JobProgress({ job, compact = false }) {
  const { approved, waiting, left } = jobProgress(job);
  const total = Math.max(job.leaflets, approved + waiting) || 1;
  return (
    <div className="lf-progress-wrap">
      <div className="lf-progress" role="img" aria-label={`${approved} approved, ${waiting} waiting, ${left} to go`}>
        <span className="approved" style={{ width: `${(approved / total) * 100}%` }} />
        <span className="waiting" style={{ width: `${(waiting / total) * 100}%` }} />
      </div>
      {!compact && (
        <div className="lf-progress-text">
          {formatCount(approved)} of {formatCount(job.leaflets)} delivered
          {waiting > 0 && <>, {formatCount(waiting)} to check</>}
        </div>
      )}
    </div>
  );
}

// ── Proof pictures ────────────────────────────────────────────────────────────
// They're private, so they come through the API with the Farm OS login rather
// than as plain image links. Each one is fetched once per session.

const photoCache = new Map();

function photoUrl(name) {
  if (!photoCache.has(name)) {
    photoCache.set(name, api.get(`/leaflets/photos/${name}`, { responseType: 'blob' })
      .then(r => URL.createObjectURL(r.data))
      .catch((e) => { photoCache.delete(name); throw e; }));
  }
  return photoCache.get(name);
}

export function usePhoto(name) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    if (!name) return undefined;
    photoUrl(name).then(u => live && setUrl(u)).catch(() => live && setFailed(true));
    return () => { live = false; };
  }, [name]);
  return { url, failed };
}

function Thumb({ photo, onClick }) {
  const { url, failed } = usePhoto(photo.thumb || photo.file);
  return (
    <button type="button" className="lf-photo" onClick={onClick} title={photo.originalName || 'Proof picture'}
      style={url ? { backgroundImage: `url(${url})` } : undefined}>
      {!url && <span>{failed ? 'Can\'t load' : 'Loading…'}</span>}
    </button>
  );
}

function PhotoViewer({ photos, start, onClose }) {
  const [i, setI] = useState(start);
  const photo = photos[i];
  const { url, failed } = usePhoto(photo.file);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setI(n => Math.min(photos.length - 1, n + 1));
      if (e.key === 'ArrowLeft') setI(n => Math.max(0, n - 1));
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photos.length, onClose]);
  return (
    <ModalShell onClose={onClose} width="auto" maxWidth="96vw" dark ariaLabel="Proof picture" panelStyle={{ padding: 12 }}>
      <div className="lf-viewer">
        {url ? <img src={url} alt={photo.originalName || 'Proof'} /> : <div className="lf-viewer__msg">{failed ? 'This picture couldn\'t be loaded.' : 'Loading…'}</div>}
        <div className="lf-viewer__bar">
          <Button size="sm" variant="muted" disabled={i === 0} onClick={() => setI(i - 1)}>Previous</Button>
          <span>{i + 1} of {photos.length}</span>
          <Button size="sm" variant="muted" disabled={i === photos.length - 1} onClick={() => setI(i + 1)}>Next</Button>
          {url && <a className="ui-btn ui-btn--sm" href={url} download={photo.originalName || photo.file}>Download</a>}
          <Button size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </ModalShell>
  );
}

export function Photos({ photos }) {
  const [open, setOpen] = useState(null);
  if (!photos?.length) return <span className="lf-muted">No pictures</span>;
  return (
    <>
      <div className="lf-photos">
        {photos.map((p, i) => <Thumb key={p.file} photo={p} onClick={() => setOpen(i)} />)}
      </div>
      {open !== null && <PhotoViewer photos={photos} start={open} onClose={() => setOpen(null)} />}
    </>
  );
}

// ── Maps ──────────────────────────────────────────────────────────────────────

// Zones drawn on a Leaflet map. items: [{ zone, colour, stroke, fill, dashed,
// label, faded }]; stroke (the outline) defaults to colour. Labels hide when
// zoomed out far enough for them to pile up.
export function ZonesLayer({ map, items, selectedId, onSelect, showRoutes = true, interactive = true }) {
  const latest = useRef(onSelect);
  latest.current = onSelect;

  useEffect(() => {
    if (!map) return undefined;
    const L = window.L;
    const group = L.layerGroup().addTo(map);
    for (const it of items) {
      const selected = it.zone._id === selectedId;
      const poly = L.polygon(it.zone.polygon, {
        color: it.stroke || it.colour,
        weight: selected ? 4 : 2.5,
        dashArray: it.dashed ? '6 5' : null,
        fillColor: it.colour,
        fillOpacity: it.fill ?? 0.2,
        opacity: it.faded ? 0.55 : 1,
        interactive,
        bubblingMouseEvents: false,
      }).addTo(group);
      if (it.label) {
        // Clicking the label selects its zone too (it's often wider than the zone)
        poly.bindTooltip(it.label, {
          permanent: true, direction: 'center', className: `lf-map-label${selected ? ' is-selected' : ''}`,
          interactive, bubblingMouseEvents: false,
        });
      }
      if (interactive) {
        const select = () => latest.current?.(it.zone._id);
        poly.on('click', select);
        poly.getTooltip()?.on('click', select);
      }
      if (showRoutes && it.zone.route?.length > 1) {
        L.polyline(it.zone.route, { color: '#113449', weight: 2, dashArray: '6 5', opacity: 0.8, interactive: false }).addTo(group);
      }
      if (selected) poly.bringToFront();
    }
    return () => group.remove();
  }, [map, items, selectedId, showRoutes, interactive]);

  useEffect(() => {
    if (!map) return undefined;
    const el = map.getContainer();
    const update = () => el.classList.toggle('lf-hide-labels', map.getZoom() < 13);
    update();
    map.on('zoomend', update);
    return () => map.off('zoomend', update);
  }, [map]);

  return null;
}

const round6 = (n) => Math.round(n * 1e6) / 1e6;

// Draws and edits one shape. kind 'polygon' is a zone outline: click the map
// to add corners, then click the first corner to close it. kind 'line' is a
// route: click along the streets in walking order. While active, drag a
// corner to move it, drag a small dot to add a corner there, and right-click
// a corner to remove it.
export function ShapeEditor({ map, kind, points, closed = false, active, colour, onChange }) {
  const isPolygon = kind === 'polygon';
  const drawing = active && (!isPolygon || !closed);
  const change = useRef(onChange);
  change.current = onChange;

  useEffect(() => {
    if (!map || !points.length) return undefined;
    const L = window.L;
    const group = L.layerGroup().addTo(map);
    const lls = points.map(p => L.latLng(p[0], p[1]));
    const shape = isPolygon && closed
      ? L.polygon(lls, { color: colour, weight: 3, fillOpacity: 0.15, interactive: false })
      : L.polyline(lls, { color: colour, weight: 3, dashArray: isPolygon ? '6 6' : '9 6', interactive: false });
    shape.addTo(group);

    if (active) {
      const fewest = isPolygon ? 3 : 2;
      points.forEach((p, i) => {
        const closer = isPolygon && !closed && i === 0 && points.length >= 3;
        const handle = L.marker(p, {
          draggable: true,
          keyboard: false,
          title: closer ? 'Click to close the outline' : 'Drag to move, right-click to remove',
          icon: L.divIcon({ className: `lf-handle${closer ? ' is-closer' : ''}`, iconSize: closer ? [16, 16] : [12, 12] }),
        }).addTo(group);
        let moved = false;
        handle.on('dragstart', () => { moved = true; });
        handle.on('drag', (e) => { lls[i] = e.target.getLatLng(); shape.setLatLngs(lls); });
        handle.on('dragend', () => change.current(lls.map(ll => [round6(ll.lat), round6(ll.lng)]), closed));
        handle.on('click', () => {
          if (moved) { moved = false; return; }
          if (closer) change.current(points, true);
        });
        handle.on('contextmenu', (e) => {
          L.DomEvent.preventDefault(e.originalEvent);
          const next = points.filter((_, j) => j !== i);
          if (closed && next.length < fewest) return;
          change.current(next, closed);
        });
      });
      const segments = isPolygon && closed ? points.length : points.length - 1;
      for (let i = 0; i < segments; i++) {
        const a = points[i];
        const b = points[(i + 1) % points.length];
        const mid = L.marker([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2], {
          draggable: true,
          keyboard: false,
          title: 'Drag to add a corner here',
          icon: L.divIcon({ className: 'lf-handle is-mid', iconSize: [10, 10] }),
        }).addTo(group);
        mid.on('drag', (e) => {
          const copy = lls.slice();
          copy.splice(i + 1, 0, e.target.getLatLng());
          shape.setLatLngs(copy);
        });
        mid.on('dragend', (e) => {
          const ll = e.target.getLatLng();
          const next = points.slice();
          next.splice(i + 1, 0, [round6(ll.lat), round6(ll.lng)]);
          change.current(next, closed);
        });
      }
    }
    return () => group.remove();
  }, [map, points, closed, active, colour, isPolygon]);

  useEffect(() => {
    if (!map || !drawing) return undefined;
    const onClick = (e) => change.current([...points, [round6(e.latlng.lat), round6(e.latlng.lng)]], false);
    map.on('click', onClick);
    const el = map.getContainer();
    el.classList.add('lf-drawing');
    return () => {
      map.off('click', onClick);
      el.classList.remove('lf-drawing');
    };
  }, [map, drawing, points]);

  return null;
}

// Labelled facts in a row: [[label, value], ...]
export const Facts = ({ items }) => (
  <div className="lf-facts">
    {items.filter(Boolean).map(([k, v]) => <div key={k}><span>{k}</span>{v}</div>)}
  </div>
);
